import React, { useMemo, useState } from 'react';
import styled, { keyframes } from 'styled-components';
import { PieChart, Pie, Cell, ResponsiveContainer, Tooltip } from 'recharts';
import { FaChartPie, FaTags, FaInbox, FaBookmark, FaPenNib, FaChevronDown } from 'react-icons/fa';
import { formatCurrency } from '../../utils/utils';

const CATEGORY_COLORS = {
  'Crew Hours':  '#5b6ee6',
  'Materials':   '#2fbf71',
  'Products':    '#f59f00',
  'Maintenance': '#e74c3c',
  'Tools':       '#8e44ad',
  'Fuel':        '#16a085',
  'Others':      '#7f8c8d',
};

const FALLBACK_COLOR = '#95a5a6';

const fadeUp = keyframes`
  from { opacity: 0; transform: translateY(8px); }
  to   { opacity: 1; transform: translateY(0); }
`;

const Wrapper = styled.section`
  background: ${(p) => p.theme.colors.surface};
  border-radius: ${(p) => p.theme.borderRadius};
  padding: 20px;
  margin: 16px 0;
  box-shadow: 0 2px 5px rgba(0, 0, 0, 0.08);
  animation: ${fadeUp} 0.35s ease-out;
`;

const Header = styled.header`
  display: flex;
  align-items: center;
  justify-content: space-between;
  flex-wrap: wrap;
  gap: 8px;
  margin-bottom: 18px;
`;

const Title = styled.h3`
  display: flex;
  align-items: center;
  gap: 10px;
  font-size: 16px;
  font-weight: 700;
  color: ${(p) => p.theme.colors.textPrimary};
  margin: 0;
  svg { color: ${(p) => p.theme.colors.primary}; }
`;

const HeaderMeta = styled.div`
  font-size: 13px;
  color: ${(p) => p.theme.colors.textSecondary};
  display: flex;
  gap: 14px;
  flex-wrap: wrap;
`;

const HeaderMetaItem = styled.span`
  strong {
    color: ${(p) => p.theme.colors.textPrimary};
    font-weight: 700;
  }
`;

const Body = styled.div`
  display: grid;
  grid-template-columns: minmax(240px, 0.9fr) minmax(0, 1.4fr);
  gap: 24px;
  align-items: start;
  @media (max-width: 760px) {
    grid-template-columns: 1fr;
  }
`;

const ChartArea = styled.div`
  position: relative;
  height: 280px;
  width: 100%;
  align-self: center;
`;

const Centerpiece = styled.div`
  position: absolute;
  inset: 0;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  pointer-events: none;
  text-align: center;
`;

const CenterLabel = styled.div`
  font-size: 11px;
  font-weight: 600;
  letter-spacing: 0.6px;
  text-transform: uppercase;
  color: ${(p) => p.theme.colors.textSecondary};
`;

const CenterValue = styled.div`
  font-size: 24px;
  font-weight: 800;
  color: ${(p) => p.theme.colors.textPrimary};
  margin-top: 2px;
`;

const CenterSub = styled.div`
  font-size: 11px;
  color: ${(p) => p.theme.colors.textSecondary};
  margin-top: 4px;
`;

const Breakdown = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

const CategoryBlock = styled.div`
  border: 1px solid ${(p) => p.theme.colors.inputGray}33;
  border-radius: 10px;
  overflow: hidden;
  background: ${(p) => p.theme.colors.background};
`;

const CategoryHeader = styled.button`
  display: grid;
  grid-template-columns: 14px 1fr auto auto;
  align-items: center;
  gap: 12px;
  width: 100%;
  padding: 12px 14px;
  background: transparent;
  border: 0;
  cursor: pointer;
  text-align: left;
  transition: background 0.15s ease;
  &:hover { background: ${(p) => p.theme.colors.surface}; }
`;

const Swatch = styled.span`
  width: 14px;
  height: 14px;
  border-radius: 4px;
  background: ${(p) => p.color};
  box-shadow: 0 0 0 2px ${(p) => p.color}22;
`;

const CategoryMain = styled.div`
  min-width: 0;
`;

const CategoryTopRow = styled.div`
  display: flex;
  align-items: baseline;
  justify-content: space-between;
  gap: 8px;
`;

const CategoryName = styled.span`
  font-size: 14px;
  font-weight: 700;
  color: ${(p) => p.theme.colors.textPrimary};
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const CategoryStats = styled.span`
  font-size: 11px;
  color: ${(p) => p.theme.colors.textSecondary};
  font-weight: 500;
`;

const Bar = styled.div`
  height: 6px;
  background: ${(p) => p.theme.colors.surface};
  border-radius: 3px;
  margin-top: 6px;
  overflow: hidden;
`;

const BarFill = styled.div`
  height: 100%;
  width: ${(p) => p.pct}%;
  background: linear-gradient(90deg, ${(p) => p.color}, ${(p) => p.color}cc);
  border-radius: 3px;
  transition: width 0.6s cubic-bezier(0.4, 0, 0.2, 1);
`;

const CategoryAmount = styled.div`
  text-align: right;
  font-variant-numeric: tabular-nums;
  font-size: 14px;
  font-weight: 700;
  color: ${(p) => p.theme.colors.textPrimary};
  span {
    display: block;
    font-size: 11px;
    color: ${(p) => p.theme.colors.textSecondary};
    font-weight: 500;
    margin-top: 2px;
  }
`;

const Chevron = styled(FaChevronDown)`
  color: ${(p) => p.theme.colors.textSecondary};
  font-size: 12px;
  transform: rotate(${(p) => (p.$open ? 180 : 0)}deg);
  transition: transform 0.2s ease;
`;

const ItemList = styled.div`
  display: ${(p) => (p.$open ? 'flex' : 'none')};
  flex-direction: column;
  gap: 0;
  padding: 0 14px 10px 40px;
  background: ${(p) => p.theme.colors.background};
`;

const SectionLabel = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
  font-size: 10px;
  font-weight: 700;
  letter-spacing: 0.6px;
  text-transform: uppercase;
  color: ${(p) => p.theme.colors.textSecondary};
  padding: 10px 0 4px;
  svg { font-size: 11px; }
`;

const Item = styled.div`
  display: grid;
  grid-template-columns: 1fr auto;
  gap: 10px;
  padding: 6px 0;
  align-items: baseline;
  border-bottom: 1px dashed ${(p) => p.theme.colors.inputGray}55;
  &:last-child { border-bottom: none; }
`;

const ItemName = styled.div`
  font-size: 13px;
  color: ${(p) => p.theme.colors.textPrimary};
  font-weight: 500;
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

const ItemMeta = styled.div`
  font-size: 11px;
  color: ${(p) => p.theme.colors.textSecondary};
  margin-top: 2px;
  font-weight: 400;
`;

const ItemAmount = styled.div`
  text-align: right;
  font-variant-numeric: tabular-nums;
  font-size: 13px;
  font-weight: 700;
  color: ${(p) => p.theme.colors.textPrimary};
`;

const EmptyState = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 32px 16px;
  color: ${(p) => p.theme.colors.textSecondary};
  text-align: center;
  svg { font-size: 32px; margin-bottom: 8px; opacity: 0.6; }
  p { margin: 0; font-size: 13px; }
`;

const SliceLabel = ({ cx, cy, midAngle, innerRadius, outerRadius, percent }) => {
  if (!percent || percent < 0.06) return null;
  const radius = innerRadius + (outerRadius - innerRadius) * 0.55;
  const x = cx + radius * Math.cos(-midAngle * Math.PI / 180);
  const y = cy + radius * Math.sin(-midAngle * Math.PI / 180);
  return (
    <text
      x={x}
      y={y}
      fill="#fff"
      textAnchor="middle"
      dominantBaseline="central"
      style={{ fontSize: 12, fontWeight: 700, textShadow: '0 1px 2px rgba(0,0,0,0.25)' }}
    >
      {`${(percent * 100).toFixed(0)}%`}
    </text>
  );
};

const buildItemNameSet = (costItems) => {
  const set = new Map();
  for (const it of costItems || []) {
    const base = (it.name || '').trim();
    if (!base) continue;
    set.set(base.toLowerCase(), it);
    if (it.unit) {
      set.set(`${base} (${it.unit})`.toLowerCase(), it);
    }
  }
  return set;
};

const matchCostItem = (description, lookup) => {
  if (!description) return null;
  return lookup.get(description.trim().toLowerCase()) || null;
};

const CostBreakdown = ({ costs, costItems = [] }) => {
  const [openCategories, setOpenCategories] = useState({});

  const { categories, total, itemCount } = useMemo(() => {
    const list = Array.isArray(costs) ? costs : [];
    const lookup = buildItemNameSet(costItems);
    const byCategory = new Map();
    let runningTotal = 0;

    for (const c of list) {
      const cat = c.category || 'Others';
      const amount =
        parseFloat(c.total_cost) ||
        (parseFloat(c.quantity) || 0) * (parseFloat(c.unit_price) || 0);
      const qty = parseFloat(c.quantity) || 0;
      const unitPrice = parseFloat(c.unit_price) || 0;
      runningTotal += amount;

      if (!byCategory.has(cat)) {
        byCategory.set(cat, {
          name: cat,
          color: CATEGORY_COLORS[cat] || FALLBACK_COLOR,
          value: 0,
          count: 0,
          catalogGroups: new Map(),
          manualLines: [],
        });
      }
      const bucket = byCategory.get(cat);
      bucket.value += amount;
      bucket.count += 1;

      const matched = matchCostItem(c.description, lookup);
      if (matched) {
        const key = matched.cost_item_id || matched.name;
        if (!bucket.catalogGroups.has(key)) {
          bucket.catalogGroups.set(key, {
            key,
            name: matched.name,
            unit: matched.unit || '',
            unitPrice,
            totalQty: 0,
            totalAmount: 0,
            entries: 0,
          });
        }
        const g = bucket.catalogGroups.get(key);
        g.totalQty += qty;
        g.totalAmount += amount;
        g.entries += 1;
        if (g.unitPrice !== unitPrice) g.unitPrice = null;
      } else {
        bucket.manualLines.push({
          id: c.cost_id || `${cat}-${bucket.manualLines.length}`,
          description: c.description || '(no description)',
          quantity: qty,
          unitPrice,
          amount,
        });
      }
    }

    const computed = Array.from(byCategory.values())
      .map((b) => ({
        ...b,
        value: Math.round(b.value * 100) / 100,
        pct: runningTotal > 0 ? (b.value / runningTotal) * 100 : 0,
        catalogGroups: Array.from(b.catalogGroups.values()).sort(
          (a, b2) => b2.totalAmount - a.totalAmount,
        ),
        manualLines: b.manualLines.sort((a, b2) => b2.amount - a.amount),
      }))
      .sort((a, b) => b.value - a.value);

    return {
      categories: computed,
      total: Math.round(runningTotal * 100) / 100,
      itemCount: list.length,
    };
  }, [costs, costItems]);

  const toggleCategory = (name) => {
    setOpenCategories((prev) => ({ ...prev, [name]: !prev[name] }));
  };

  if (categories.length === 0) {
    return (
      <Wrapper>
        <Header>
          <Title><FaChartPie /> Cost Breakdown by Category</Title>
        </Header>
        <EmptyState>
          <FaInbox />
          <p>No costs yet — add items above to see the breakdown.</p>
        </EmptyState>
      </Wrapper>
    );
  }

  const topCat = categories[0];

  return (
    <Wrapper>
      <Header>
        <Title><FaChartPie /> Cost Breakdown by Category</Title>
        <HeaderMeta>
          <HeaderMetaItem>
            <FaTags style={{ marginRight: 4, opacity: 0.7 }} />
            <strong>{categories.length}</strong>{' '}
            {categories.length === 1 ? 'category' : 'categories'}
          </HeaderMetaItem>
          <HeaderMetaItem>
            <strong>{itemCount}</strong>{' '}
            {itemCount === 1 ? 'line item' : 'line items'}
          </HeaderMetaItem>
          <HeaderMetaItem>
            Largest:{' '}
            <strong style={{ color: topCat.color }}>{topCat.name}</strong>
          </HeaderMetaItem>
        </HeaderMeta>
      </Header>

      <Body>
        <ChartArea>
          <ResponsiveContainer width="100%" height="100%">
            <PieChart>
              <Pie
                data={categories}
                dataKey="value"
                nameKey="name"
                cx="50%"
                cy="50%"
                innerRadius="62%"
                outerRadius="92%"
                paddingAngle={categories.length > 1 ? 2 : 0}
                stroke="none"
                isAnimationActive
                labelLine={false}
                label={SliceLabel}
              >
                {categories.map((row) => (
                  <Cell key={row.name} fill={row.color} />
                ))}
              </Pie>
              <Tooltip
                cursor={{ fill: 'rgba(0,0,0,0.04)' }}
                formatter={(value, name) => [formatCurrency(value), name]}
                contentStyle={{
                  background: '#1f2937',
                  border: 'none',
                  borderRadius: 8,
                  padding: '8px 12px',
                  color: '#fff',
                  fontSize: 12,
                  boxShadow: '0 4px 12px rgba(0,0,0,0.18)',
                }}
                itemStyle={{ color: '#fff' }}
                labelStyle={{ color: '#cbd5e1' }}
              />
            </PieChart>
          </ResponsiveContainer>
          <Centerpiece>
            <CenterLabel>Total Costs</CenterLabel>
            <CenterValue>{formatCurrency(total)}</CenterValue>
            <CenterSub>
              across {categories.length}{' '}
              {categories.length === 1 ? 'category' : 'categories'}
            </CenterSub>
          </Centerpiece>
        </ChartArea>

        <Breakdown>
          {categories.map((cat) => {
            const isOpen = openCategories[cat.name] !== false;
            const hasCatalog = cat.catalogGroups.length > 0;
            const hasManual = cat.manualLines.length > 0;
            return (
              <CategoryBlock key={cat.name}>
                <CategoryHeader
                  type="button"
                  onClick={() => toggleCategory(cat.name)}
                  aria-expanded={isOpen}
                >
                  <Swatch color={cat.color} />
                  <CategoryMain>
                    <CategoryTopRow>
                      <CategoryName>{cat.name}</CategoryName>
                      <CategoryStats>
                        {cat.count} {cat.count === 1 ? 'item' : 'items'}
                        {hasCatalog ? ` · ${cat.catalogGroups.length} saved` : ''}
                        {hasManual ? ` · ${cat.manualLines.length} manual` : ''}
                      </CategoryStats>
                    </CategoryTopRow>
                    <Bar>
                      <BarFill color={cat.color} pct={cat.pct} />
                    </Bar>
                  </CategoryMain>
                  <CategoryAmount>
                    {formatCurrency(cat.value)}
                    <span>{cat.pct.toFixed(1)}%</span>
                  </CategoryAmount>
                  <Chevron $open={isOpen} />
                </CategoryHeader>

                <ItemList $open={isOpen}>
                  {hasCatalog && (
                    <>
                      <SectionLabel>
                        <FaBookmark /> Saved cost items
                      </SectionLabel>
                      {cat.catalogGroups.map((g) => (
                        <Item key={`cat-${g.key}`}>
                          <div>
                            <ItemName>{g.name}</ItemName>
                            <ItemMeta>
                              {g.totalQty} {g.unit || 'units'}
                              {g.unitPrice != null && ` × ${formatCurrency(g.unitPrice)}`}
                              {g.entries > 1 ? ` · ${g.entries} entries` : ''}
                            </ItemMeta>
                          </div>
                          <ItemAmount>{formatCurrency(g.totalAmount)}</ItemAmount>
                        </Item>
                      ))}
                    </>
                  )}

                  {hasManual && (
                    <>
                      <SectionLabel>
                        <FaPenNib /> Manual entries
                      </SectionLabel>
                      {cat.manualLines.map((m) => (
                        <Item key={`man-${m.id}`}>
                          <div>
                            <ItemName>{m.description}</ItemName>
                            <ItemMeta>
                              {m.quantity || 0} × {formatCurrency(m.unitPrice)}
                            </ItemMeta>
                          </div>
                          <ItemAmount>{formatCurrency(m.amount)}</ItemAmount>
                        </Item>
                      ))}
                    </>
                  )}
                </ItemList>
              </CategoryBlock>
            );
          })}
        </Breakdown>
      </Body>
    </Wrapper>
  );
};

export default CostBreakdown;
